export interface TileId {
  x: number
  y: number
  z: number
}

export interface TileBBox {
  long1: number // west longitude  (topLeft.lng)
  lat1: number // north latitude  (topLeft.lat)
  long2: number // east longitude  (bottomRight.lng)
  lat2: number // south latitude  (bottomRight.lat)
}

// Web Mercator is undefined beyond ±85.05112878°; clamp to prevent NaN/Infinity
const MAX_MERCATOR_LAT = 85.05112878

export function latLngToTileXY(
  lat: number,
  lng: number,
  zoom: number,
): { x: number; y: number } {
  const clampedLat = Math.max(
    -MAX_MERCATOR_LAT,
    Math.min(MAX_MERCATOR_LAT, lat),
  )
  const n = Math.pow(2, zoom)
  const x = Math.floor(((lng + 180) / 360) * n)
  const latRad = (clampedLat * Math.PI) / 180
  const y = Math.floor(
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n,
  )
  return {
    x: Math.max(0, Math.min(n - 1, x)),
    y: Math.max(0, Math.min(n - 1, y)),
  }
}

export function tileToBBox(x: number, y: number, z: number): TileBBox {
  const n = Math.pow(2, z)
  const long1 = (x / n) * 360 - 180
  const long2 = ((x + 1) / n) * 360 - 180
  const lat1 =
    (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) * 180) / Math.PI
  const lat2 =
    (Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 1)) / n))) * 180) / Math.PI
  return { long1, lat1, long2, lat2 }
}

export function getTilesInViewport(
  topLeftLat: number,
  topLeftLng: number,
  bottomRightLat: number,
  bottomRightLng: number,
  zoom: number,
): TileId[] {
  const z = Math.floor(zoom) // MapLibre returns fractional zoom; tile keys must use integer z
  const n = Math.pow(2, z)
  const tl = latLngToTileXY(topLeftLat, topLeftLng, z)
  const br = latLngToTileXY(bottomRightLat, bottomRightLng, z)
  const tiles: TileId[] = []

  // Handle antimeridian crossing: when west lng > east lng, tl.x > br.x
  const xTiles: number[] = []
  if (tl.x <= br.x) {
    for (let x = tl.x; x <= br.x; x++) xTiles.push(x)
  } else {
    // Viewport wraps: left side → end of world, then 0 → right side
    for (let x = tl.x; x < n; x++) xTiles.push(x)
    for (let x = 0; x <= br.x; x++) xTiles.push(x)
  }

  for (const x of xTiles) {
    for (let y = tl.y; y <= br.y; y++) {
      tiles.push({ x, y, z })
    }
  }
  return tiles
}
